import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BRAND, Logo, formatCurrency } from '@careandkey/shared';
import { supabase } from '../lib/supabase';

// Branded, printable quote (window.print → "Save as PDF"). Ops view.
type Q = {
  quote_number: string; title: string | null; status: string; currency: string; total: number; valid_until: string | null; terms: string | null;
  clients: { name: string } | null; properties: { name: string } | null;
  quote_lines: { description: string; quantity: number; unit_price: number; line_total: number; position: number }[];
};

export function QuotePrint() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const [q, setQ] = useState<Q | null>(null);

  useEffect(() => {
    (async () => {
      const r = await supabase.from('quotes').select('quote_number,title,status,currency,total,valid_until,terms,clients(name),properties(name),quote_lines(description,quantity,unit_price,line_total,position)').eq('id', id).single();
      setQ(r.data as unknown as Q);
    })();
  }, [id]);

  useEffect(() => { if (q) { const t = setTimeout(() => window.print(), 400); return () => clearTimeout(t); } }, [q]);

  const C = BRAND.colors;
  if (!q) return <div className="p-10 text-muted">…</div>;
  const lines = [...(q.quote_lines ?? [])].sort((a, b) => a.position - b.position);
  return (
    <div className="mx-auto max-w-[800px] bg-white p-10 text-[#0B0F17]" style={{ fontFamily: 'Geist, system-ui, sans-serif' }}>
      <div className="flex items-start justify-between border-b-2 pb-5" style={{ borderColor: C.ink }}>
        <Logo variant="light" size={34} />
        <div className="text-right">
          <div className="text-[20px] font-semibold">{t('nav.quotes')}</div>
          <div className="font-mono text-sm text-[#667085]">{q.quote_number}</div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
        <div><div className="text-xs text-[#667085]">{t('nav.clients')}</div><div className="font-medium">{q.clients?.name ?? '—'}</div></div>
        <div><div className="text-xs text-[#667085]">{t('nav.properties')}</div><div className="font-medium">{q.properties?.name ?? '—'}</div></div>
        {q.title && <div><div className="text-xs text-[#667085]">{t('quote.title')}</div><div className="font-medium">{q.title}</div></div>}
        {q.valid_until && <div><div className="text-xs text-[#667085]">{t('quote.validUntil')}</div><div className="font-mono">{q.valid_until}</div></div>}
      </div>

      <table className="mt-6 w-full border-collapse text-sm">
        <thead><tr style={{ background: '#F7F8FA' }} className="text-left text-xs text-[#667085]"><th className="p-2.5">{t('cost.description')}</th><th className="p-2.5 text-right">{t('cost.quantity')}</th><th className="p-2.5 text-right">{t('cat.unitPrice')}</th><th className="p-2.5 text-right">{t('cost.total')}</th></tr></thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={i} className="border-b border-[#EAECF0]">
              <td className="p-2.5">{l.description}</td>
              <td className="p-2.5 text-right font-mono">{l.quantity}</td>
              <td className="p-2.5 text-right font-mono">{formatCurrency(l.unit_price, q.currency, i18n.language)}</td>
              <td className="p-2.5 text-right font-mono">{formatCurrency(l.line_total, q.currency, i18n.language)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-4 flex justify-end"><span className="text-lg font-semibold" style={{ color: C.accent }}>{t('cost.total')}: {formatCurrency(q.total, q.currency, i18n.language)}</span></div>
      {q.terms && <p className="mt-6 whitespace-pre-wrap text-sm text-[#667085]">{q.terms}</p>}
      <p className="mt-10 text-center text-xs text-[#98A2B3]">{BRAND.name} · {BRAND.domain}</p>
    </div>
  );
}
